import React from "react";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineDot from "@mui/lab/TimelineDot";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import InstallMobileOutlinedIcon from "@mui/icons-material/InstallMobileOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";

import ROM from "./ROM";
import { GROUP_BY_TIME } from "./importTimeline";
import NoteContent from "./Note/NoteContent";
import { noteColors } from "./Note/Components/ColorSelector";
import { ListItem } from "@mui/material";
import TimelineText from "./TimelineItem/TimelineText";

const timelineOppositeContentWidth = 70;
const timelineSeparatorWidth = 47;

// Render a timeline item
function CustomTimelineItem({
  Icon,
  color,
  Item,
  isFirstEventItem,
  isLastEventItem,
  isLastDateItem,
}) {
  return (
    <TimelineItem sx={{ minHeight: 0 }}>
      <TimelineOppositeContent
        sx={{
          justifyContent: "end",
          display: "flex",
          minWidth: timelineOppositeContentWidth,
          maxWidth: timelineOppositeContentWidth,
          padding: 1,
          minHeight: 0,
        }}
      ></TimelineOppositeContent>
      <TimelineSeparator
        sx={{
          maxWidth: timelineSeparatorWidth,
          minWidth: timelineSeparatorWidth,
        }}
      >
        <TimelineDot
          sx={{
            backgroundColor: color,
            color: "#fff",
            marginBottom: 0,
            marginTop: 0,
            alignSelf: "center",
          }}
        >
          {Icon}
        </TimelineDot>
        {isLastDateItem && isLastEventItem ? null : <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent
        sx={{ padding: "0px 16px" }}
        style={isLastEventItem && !isLastDateItem ? { paddingBottom: 90 } : {}}
      >
        <Card
          sx={{
            height: "100%",
            width: "100%",
            borderRadius: isFirstEventItem && isLastEventItem ? 8 : 0,
            borderTopLeftRadius: isFirstEventItem ? 8 : 0,
            borderTopRightRadius: isFirstEventItem ? 8 : 0,
            borderBottomLeftRadius: isLastEventItem ? 8 : 0,
            borderBottomRightRadius: isLastEventItem ? 8 : 0,
            borderBottom: isLastEventItem
              ? "0px solid rgba(0, 0, 0, 0.12)"
              : "1px solid rgba(0, 0, 0, 0.12)",
          }}
          elevation={1}
        >
          {Item}
        </Card>
      </TimelineContent>
    </TimelineItem>
  );
}

// Render the timeline dot
function CustomTimelineDot({ Icon, amount }) {
  return (
    <Box
      width={24}
      height={24}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {Icon ? (
        <Icon />
      ) : (
        <Typography variant="subtitle2" component="div" sx={{ color: "#fff" }}>
          {amount}x
        </Typography>
      )}
    </Box>
  );
}

/** What to render? Based on event.type:
 * 0 = intake
 * 1 = survey
 * 2 = button (if itemId is NULL => counterbutton)
 * 3 = applet
 * 4 = button install
 * 5 = applet install
 * 6 = note
 *
 * */
function EventItem(props) {
  const {
    event,
    surveys,
    filterOnView,
    initExpanded,
    carers,
    practitionerCode,
    isFirstEventItem,
    isLastEventItem,
    isLastDateItem,
  } = props;
  const defaultEventProps = {
    isFirstEventItem,
    isLastEventItem,
    isLastDateItem,
  };
  const creator =
    carers?.[event.carerId]?.code &&
    carers?.[event.carerId]?.code !== practitionerCode
      ? `${carers[event.carerId].firstName} ${
          carers[event.carerId].lastName
        } @${carers[event.carerId].code}`
      : "";
  if (event.type === 2 && event.itemId === null) {
    // Counter button
    return (
      <CustomTimelineItem
        {...defaultEventProps}
        Icon={
          <CustomTimelineDot
            Icon={
              [GROUP_BY_TIME].includes(filterOnView)
                ? TouchAppOutlinedIcon
                : null
            }
            amount={surveys.length}
          />
        }
        color="items.button"
        Item={
          <ListItem sx={{ display: "block" }}>
            <TimelineText
              title={event.title}
              creator={creator}
              timestamps={{ start: event.time }}
              type={event.type}
            />
          </ListItem>
        }
      />
    );
  }
  // Survey or button response
  if ([0, 1, 2, 3].includes(event.type)) {
    return (
      <CustomTimelineItem
        {...defaultEventProps}
        Icon={
          surveys.length === 1 ? (
            <AssignmentOutlinedIcon />
          ) : (
            <CustomTimelineDot
              Icon={
                [GROUP_BY_TIME].includes(filterOnView)
                  ? LibraryBooksOutlinedIcon
                  : null
              }
              amount={surveys.length}
            />
          )
        }
        color="items.survey"
        Item={
          <ROM
            event={event}
            creator={creator}
            surveys={surveys}
            filterOnView={filterOnView}
            initExpanded={initExpanded}
          />
        }
      />
    );
  }
  // install button
  if (event.type === 4) {
    return (
      <CustomTimelineItem
        {...defaultEventProps}
        Icon={<InstallMobileOutlinedIcon />}
        color="items.button"
        Item={
          <ListItem sx={{ display: "block" }}>
            <TimelineText
              title={event.title}
              creator={creator}
              timestamps={{ start: event.time }}
              type={event.type}
            />
          </ListItem>
        }
      />
    );
  }
  // install applet
  if (event.type === 5) {
    return (
      <CustomTimelineItem
        {...defaultEventProps}
        Icon={<InstallMobileOutlinedIcon />}
        color="items.app"
        Item={
          <ListItem sx={{ display: "block" }}>
            <TimelineText
              title={event.title}
              creator={creator}
              timestamps={{ start: event.time }}
              type={event.type}
            />
          </ListItem>
        }
      />
    );
  }
  // note
  if (event.type === 6) {
    return (
      <CustomTimelineItem
        {...defaultEventProps}
        Icon={<CreateOutlinedIcon />}
        color={noteColors[event.color]}
        Item={<NoteContent event={event} filterOnView={filterOnView} />}
      />
    );
  }
  // undefined timeline item
  return (
    <CustomTimelineItem
      {...defaultEventProps}
      Icon={<HelpOutlineOutlinedIcon />}
      color="item.survey"
      Item={<Typography>This item is not supported</Typography>}
    />
  );
}

EventItem.propTypes = {
  event: PropTypes.object.isRequired,
};

export default EventItem;
